import { FormHeader } from 'components/common';
import {
  useTaskApproveQuery,
  useTaskConditionalReviewQuery,
  useTaskCreateQuery,
  useTaskDeleteQuery,
  useTaskRejectQuery,
  useTaskReopenQuery,
  useTaskReviewQuery,
  useTaskUpdateQuery,
} from 'hooks';
import { validateHTML } from 'hooks/helpers/common';
import { useModal } from 'hooks/ui/useModal';
import { AllStatuses } from 'interfaces';
import {
  setTaskErrors,
  useTaskStore,
  validatePredefinedOption,
  validateTask,
  validateTitle,
} from 'store/task';

export const TaskHeader = () => {
  const modal = useModal();
  const { draft, assignedGroup } = useTaskStore((state) => state);
  const { mutate: sendTaskForReview } = useTaskReviewQuery();
  const { mutate: sendTaskWithCondition } = useTaskConditionalReviewQuery();
  const { mutate: createTask } = useTaskCreateQuery({ keepActive: true });
  const { mutate: updateTask } = useTaskUpdateQuery();
  const { mutate: deleteTask, discardDraft } = useTaskDeleteQuery();
  const { mutate: approveTask } = useTaskApproveQuery();
  const { mutate: rejectTask } = useTaskRejectQuery();
  const { mutate: reopenTask } = useTaskReopenQuery();

  const validateAllFields = (option: boolean) => {
    const taskErrors = validateTask(draft, option);
    let predefinedErrors = {};

    if (draft.answers.predefinedOptions) {
      predefinedErrors = validatePredefinedOption(
        draft.answers.predefinedOptions,
        option,
      );
    }
    const hasError = [
      ...Object.values(taskErrors),
      ...Object.values(predefinedErrors),
    ].some((err) => err);

    if (hasError) {
      setTaskErrors({ ...taskErrors, ...predefinedErrors });
    }

    return hasError;
  };

  const handleSaveAsDraft = () => {
    const draftTitle = draft.title.trim();
    const titleError = validateTitle(draftTitle, true);

    if (titleError) {
      return setTaskErrors({
        title: titleError,
      });
    }

    const hasError = validateAllFields(false);

    if (hasError) return null;

    return modal.confirm({
      text: `Are you sure you want to save "${validateHTML(
        draftTitle,
      )}" as a Draft?`,
      highlights: [`"${validateHTML(draftTitle)}"`, 'save', 'Draft'],
      successAction: 'Yes',
      onSuccess: () => {
        if (draft.id) {
          return updateTask({ task: draft, assignedGroup });
        }

        return createTask(draft);
      },
    });
  };

  const handleSaveForApprove = () => {
    const hasError = validateAllFields(true);

    if (!hasError) {
      modal.confirm({
        text: `Are you sure you want to save "${validateHTML(
          draft.title,
        )}" for Approve?
                    You will not be able to edit it anymore.`,
        highlights: [`"${validateHTML(draft.title)}"`, 'save', 'Approve'],
        successAction: 'Yes',
        onSuccess: () => {
          if (draft.status === AllStatuses.NOT_SAVED) {
            return sendTaskWithCondition({
              task: draft,
              new: !draft.id,
            });
          }

          return sendTaskForReview(draft.id);
        },
      });
    }
  };

  const handleReject = () =>
    modal.confirm({
      text: `Are you sure you want to Reject "${validateHTML(draft?.title)}"?
    This action will reopen task for editing.`,
      highlights: [`"${validateHTML(draft?.title)}"`, 'Reject'],
      successAction: 'Yes',
      onSuccess: () => rejectTask(draft.id),
    });

  const handleApprove = () =>
    modal.confirm({
      text: `Are you sure you want to Approve "${validateHTML(draft.title)}"?`,
      highlights: [`"${validateHTML(draft.title)}"`, 'Approve'],
      successAction: 'Yes',
      onSuccess: () => approveTask(draft.id),
    });

  const handleSendToRework = () =>
    modal.confirm({
      text: `Are you sure you want to Reopen "${validateHTML(draft.title)}"?`,
      highlights: [`"${validateHTML(draft.title)}"`, 'Reopen'],
      successAction: 'Yes',
      onSuccess: () => reopenTask(draft.id),
    });

  const handleDelete = () => {
    if (draft.id) {
      return modal.delete({
        text: `Are you sure you want to Delete ${
          draft.title ? `"${validateHTML(draft.title)}"` : 'this task'
        }?`,
        highlights: [
          ` ${draft.title ? `"${validateHTML(draft.title)}"` : 'this task'}`,
          'Delete',
        ],
        onSuccess: () => deleteTask(draft.id),
      });
    }

    return modal.delete({
      text: 'Are you sure you want to Delete this draft?',
      highlights: ['Delete', 'draft'],
      onSuccess: () => discardDraft(),
    });
  };

  return (
    <FormHeader
      draft={draft}
      handleApprove={handleApprove}
      handleDelete={handleDelete}
      handleReject={handleReject}
      handleSaveAsDraft={handleSaveAsDraft}
      handleSaveForApprove={handleSaveForApprove}
      handleSendToRework={handleSendToRework}
    />
  );
};
